import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// UI
import { Button } from "ui/components/Button/Button";
import { NumberField } from "ui/components/TextField/NumberField";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";

import { EcoFeeEditCardProps } from "./types";
import { updateEcoFeeSettingsAPI } from "services/api/settings/ecoFee/api";
import { EcoFeeSettings } from "services/api/settings/ecoFee/types";

const EcoFeeEditCard: React.FC<EcoFeeEditCardProps> = (props) => {
  const { show, settings, onSuccess, onClose } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<EcoFeeSettings>(settings);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (show) {
      setFormValues(settings);
    }
  }, [show, settings]);

  const handleChange = useCallback((value: number | null, name?: string) => {
    if (!name) return;

    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    setIsLoading(true);

    try {
      await updateEcoFeeSettingsAPI(formValues, t);
      if (onSuccess) onSuccess();
    } catch {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  return (
    <Modal show={show} onHide={onClose}>
      <ModalHeader
        title="Ažuriranje naknade za zaštitu okoliša"
        icon="/media/images/add-icon.svg"
        onHide={onClose}
      />
      <ModalBody>
        <div className="flex gap-4 ">
          <div className="w-full m-auto h-screen">
            <div className="flex gap-3 h-full">
              <form noValidate className="text-left flex-1" onSubmit={handleFormSubmit}>
                <div className="p-4">
                  <p className="text-xs text-slate-500 mb-4">
                    Obračunska masa = masa vozila − odbitak za vozača − odbitak za gorivo
                    (odbitak za gorivo ne vrijedi za električna vozila). Naknada = obračunska
                    masa × €/kg + broj guma × stopa po gumi.
                  </p>

                  <div className="mb-3">
                    <NumberField
                      name="perKgRate"
                      label="Naknada po masi"
                      value={formValues.perKgRate}
                      onChange={handleChange}
                      groupAppend="€/kg"
                      decimals={4}
                    />
                  </div>

                  <div className="flex gap-4 mb-3">
                    <div className="flex-1">
                      <NumberField
                        name="driverMassDeductionKg"
                        label="Odbitak mase – vozač"
                        value={formValues.driverMassDeductionKg}
                        onChange={handleChange}
                        groupAppend="kg"
                        decimals={0}
                      />
                    </div>
                    <div className="flex-1">
                      <NumberField
                        name="fuelMassDeductionKg"
                        label="Odbitak mase – gorivo"
                        value={formValues.fuelMassDeductionKg}
                        onChange={handleChange}
                        groupAppend="kg"
                        decimals={0}
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 mb-3">
                    <div className="flex-1">
                      <NumberField
                        name="tireFeeM1"
                        label="Guma – M1 (osobni)"
                        value={formValues.tireFeeM1}
                        onChange={handleChange}
                        groupAppend="€/guma"
                        decimals={2}
                      />
                    </div>
                    <div className="flex-1">
                      <NumberField
                        name="tireFeeN1"
                        label="Guma – N1 (dostavni)"
                        value={formValues.tireFeeN1}
                        onChange={handleChange}
                        groupAppend="€/guma"
                        decimals={2}
                      />
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                  <Button
                    variant="outlined"
                    children={t("admin.close")}
                    onClick={() => onClose()}
                  />
                  <Button
                    variant="primary"
                    children={t("admin.save")}
                    type="submit"
                    disabled={isLoading}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default memo(EcoFeeEditCard);
